// ============================================================================
// GST CREATE SUBMISSIONS — G-1 slice 3b (ADR-0025 §3, §8)
// ============================================================================
// Turns a received document into submissions. The user declares the scope —
// GSTIN, tax period, branch — and, per sheet, which stream it feeds; the
// server makes one submission per stream from one read of the file.
//
// Two decisions the dialog makes explicit rather than leaving as traps:
//
//   BRANCH. Same GSTIN + period + branch + stream is a REVISION of what is
//   there; a different branch is a SIBLING that consolidates into the same
//   return. When the GSTIN already has files, the picker lists their branches
//   and nothing is preselected, so "add the second branch" is a choice made on
//   purpose, not a replace that happened because the field said MAIN.
//
//   STREAM PER SHEET. A Fairbooks dump carries sales, sales returns and
//   purchases in one workbook. Each sheet gets its own stream, pre-guessed
//   from its name; sheets sharing a stream go into one submission.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { gstEngagementService } from 'api/services/gstEngagementService';
import { gstSubmissionService } from 'api/services/gstSubmissionService';
import {
  GST_DOCUMENT_TYPES,
  type GstDataSubmission,
  type GstEngagement,
  type GstEngagementRegistration,
  type GstFilingPeriod,
  type GstSheetInfo,
  type GstSourceDocument,
  type GstSubmissionStreamRequest,
} from 'api/types/gstEngagement.types';

interface Props {
  isOpen: boolean;
  engagement: GstEngagement | null;
  document: GstSourceDocument | null;
  /**
   * The scope receiving already knows (ADR-0025 R1.2): the expected file's
   * GSTIN and branch, the period chosen at upload, and sheet → stream as far
   * as the file's layout is learned. What is chosen here is learned for the
   * next file.
   */
  prefill?: {
    registrationId: string;
    sourceRef: string;
    taxPeriodStart: string;
    taxPeriodEnd: string;
    sheetStreams?: Record<string, string>;
  } | null;
  /** A source-setup sample (ADR-0025 R1.4): no branch question, nothing to replace. */
  sample?: boolean;
  onClose: () => void;
  onCreated: (submissions: GstDataSubmission[]) => void;
}

const REVISION_REASONS = [
  { value: 'CLIENT_ERROR', label: 'Client sent corrected data' },
  { value: 'FIRM_QUERY', label: 'Firm queried, client re-sent' },
  { value: 'LATE_INVOICES', label: 'Late invoices added' },
  { value: 'CLIENT_RESTATEMENT', label: 'Client restated the period' },
  { value: 'OTHER', label: 'Other' },
];

const CUSTOM_PERIOD = '__custom__';
const NEW_BRANCH = '__new__';
const SKIP = '';

const inputDate = (iso: string): string => iso.slice(0, 10);
const typeLabel = (v: string) => GST_DOCUMENT_TYPES.find((t) => t.value === v)?.label ?? v;
const cleanRef = (s: string) => s.toUpperCase().replace(/[^A-Z0-9_]/g, '');

const STREAM_OPTIONS = [
  { value: SKIP, label: '— Skip this sheet —' },
  ...GST_DOCUMENT_TYPES.map((t) => ({ value: t.value, label: `${t.group} · ${t.label}` })),
];

interface Replacement { documentType: string; existing: GstDataSubmission; next: number; reparse: 'NONE' | 'REPEAT' | 'CORRECTION' }

const GstCreateSubmissionDialog: React.FC<Props> = ({ isOpen, engagement, document, prefill = null, sample = false, onClose, onCreated }) => {
  const [registrations, setRegistrations] = useState<GstEngagementRegistration[]>([]);
  const [periods, setPeriods] = useState<GstFilingPeriod[]>([]);
  const [sheets, setSheets] = useState<GstSheetInfo[]>([]);
  const [branches, setBranches] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  // Bumped when an open's load completes. The branch fetch keys on it, not on
  // the GSTIN alone: the dialog stays mounted between opens, so a second file
  // for the SAME GSTIN would otherwise never re-fetch its branches, and the
  // picker would silently fall back to a MAIN text box.
  const [loadSeq, setLoadSeq] = useState(0);

  const [registrationId, setRegistrationId] = useState('');
  const [periodKey, setPeriodKey] = useState('');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [branchChoice, setBranchChoice] = useState('');
  const [newBranch, setNewBranch] = useState('');
  const [sheetStream, setSheetStream] = useState<Record<string, string>>({});
  const [summaryBasis, setSummaryBasis] = useState('GROSS');

  const [working, setWorking] = useState(false);
  const [replacing, setReplacing] = useState<Replacement[] | null>(null);
  const [revisionReason, setRevisionReason] = useState('');
  const [revisionNote, setRevisionNote] = useState('');
  const [altBranch, setAltBranch] = useState('');

  useEffect(() => {
    if (!isOpen || !engagement || !document) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [fresh, fps, sh] = await Promise.all([
          gstEngagementService.getById(engagement.id),
          gstEngagementService.getFilingPeriods(engagement.id),
          gstSubmissionService.listSheets(engagement.id, document.id),
        ]);
        if (cancelled) return;
        const regs = fresh.registrations ?? [];
        setRegistrations(regs);
        setPeriods(fps);
        setSheets(sh);
        // The file's learned layout wins over a guess from the sheet's name.
        const known = prefill?.sheetStreams ?? {};
        const learnedAny = Object.keys(known).length > 0;
        setSheetStream(Object.fromEntries(sh.map((s) => {
          const k = Object.keys(known).find((n) => n.toLowerCase() === s.name.toLowerCase());
          return [s.name, k ? known[k] : learnedAny ? SKIP : (s.suggestedDocumentType ?? SKIP)];
        })));
        if (prefill) {
          setRegistrationId(prefill.registrationId);
          const key = `${inputDate(prefill.taxPeriodStart)}|${inputDate(prefill.taxPeriodEnd)}`;
          if (fps.some((p) => `${inputDate(p.taxPeriodStart)}|${inputDate(p.taxPeriodEnd)}` === key)) {
            setPeriodKey(key); setCustomStart(''); setCustomEnd('');
          } else {
            setPeriodKey(CUSTOM_PERIOD); setCustomStart(inputDate(prefill.taxPeriodStart)); setCustomEnd(inputDate(prefill.taxPeriodEnd));
          }
        } else {
          setRegistrationId(regs.length === 1 ? regs[0].registrationId : '');
          setPeriodKey(''); setCustomStart(''); setCustomEnd('');
        }
        setSummaryBasis('GROSS');
        setReplacing(null); setRevisionReason(''); setRevisionNote(''); setAltBranch('');
        setLoadSeq((n) => n + 1);
      } catch (err: any) {
        if (!cancelled) notificationService.toast.error(err?.response?.data?.message ?? 'Could not read the file.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, engagement, document]);

  // Branches follow the GSTIN. With none yet, the first file's branch defaults
  // to MAIN; with some, nothing is preselected (see header).
  useEffect(() => {
    if (!isOpen || !engagement || !registrationId || loading) { setBranches([]); return; }
    let cancelled = false;
    gstSubmissionService.getBranches(engagement.id, registrationId)
      .then((b) => {
        if (cancelled) return;
        setBranches(b);
        // Received as an expected file: the branch is the file's.
        if (prefill && prefill.registrationId === registrationId) {
          if (b.includes(prefill.sourceRef)) { setBranchChoice(prefill.sourceRef); setNewBranch(''); }
          else { setBranchChoice(NEW_BRANCH); setNewBranch(prefill.sourceRef); }
        } else if (b.length === 0) { setBranchChoice(NEW_BRANCH); setNewBranch('MAIN'); }
        else { setBranchChoice(''); setNewBranch(''); }
      })
      .catch(() => { if (!cancelled) { setBranches([]); setBranchChoice(NEW_BRANCH); setNewBranch('MAIN'); } });
    return () => { cancelled = true; };
    // prefill is read for the branch only on the open it came with (loadSeq).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, engagement, registrationId, loading, loadSeq]);

  const registrationOptions = useMemo(
    () => registrations.map((r) => ({ value: r.registrationId, label: r.tradeName ? `${r.gstin} · ${r.tradeName}` : r.gstin })),
    [registrations],
  );

  const periodOptions = useMemo(() => {
    const reg = registrations.find((r) => r.registrationId === registrationId);
    const seen = new Map<string, string>();
    for (const p of periods) {
      if (reg && p.clientRegistrationId && p.clientRegistrationId !== reg.clientRegistrationId) continue;
      const key = `${inputDate(p.taxPeriodStart)}|${inputDate(p.taxPeriodEnd)}`;
      if (!seen.has(key)) seen.set(key, p.periodLabel);
    }
    const opts = [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([value, label]) => ({ value, label }));
    opts.push({ value: CUSTOM_PERIOD, label: 'Other dates…' });
    return opts;
  }, [periods, registrations, registrationId]);

  const branchOptions = useMemo(
    () => [...branches.map((b) => ({ value: b, label: `${b} — revise this branch's files` })), { value: NEW_BRANCH, label: 'New branch…' }],
    [branches],
  );

  const sourceRef = branchChoice === NEW_BRANCH ? cleanRef(newBranch) : branchChoice;

  // Sheets grouped by the stream each was given: one submission per group.
  const streams: GstSubmissionStreamRequest[] = useMemo(() => {
    const groups = new Map<string, string[]>();
    for (const s of sheets) {
      const t = sheetStream[s.name];
      if (!t) continue;
      groups.set(t, [...(groups.get(t) ?? []), s.name]);
    }
    return [...groups.entries()].map(([documentType, sheetNames]) => ({
      documentType, sheetNames, summaryBasis: documentType === 'HSN_SUMMARY' ? summaryBasis : null,
    }));
  }, [sheets, sheetStream, summaryBasis]);

  const resolvedPeriod = (): { start: string; end: string } | null => {
    if (periodKey === CUSTOM_PERIOD) return customStart && customEnd ? { start: customStart, end: customEnd } : null;
    if (!periodKey) return null;
    const [start, end] = periodKey.split('|');
    return { start, end };
  };

  // A sample's branch code is set by the server; MAIN only satisfies the form.
  const effectiveRef = sample ? 'MAIN' : sourceRef;
  const canSubmit = !!registrationId && !!resolvedPeriod() && !!effectiveRef && streams.length > 0;

  const run = useCallback(async (branch: string, confirmSupersede: boolean) => {
    if (!engagement || !document) return;
    const period = resolvedPeriod();
    if (!period) return;
    setWorking(true);
    try {
      // A sample never replaces anything, so there is nothing to check first.
      if (!confirmSupersede && !sample) {
        const checks = await Promise.all(streams.map((s) => gstSubmissionService.checkScope(engagement.id, {
          registrationId, taxPeriodStart: period.start, taxPeriodEnd: period.end, sourceRef: branch, documentType: s.documentType,
          sourceDocumentId: document.id, sheetNames: s.sheetNames,
        }).then((c) => ({ s, c }))));

        // The same file with the same sheets is already parsed: nothing new
        // arrived, so there is nothing to replace. Say so and stop.
        const repeats = checks.filter((x) => x.c.reparse === 'REPEAT' && x.c.existing);
        if (repeats.length > 0) {
          const what = repeats.map((x) => `${typeLabel(x.s.documentType)} revision ${x.c.existing!.submissionNo}`).join(', ');
          notificationService.toast.warning(
            `${document.fileName} is already parsed for this period and branch (${what}). Nothing was written — open it from Submissions, or pick different sheets if the first parse read the wrong ones.`,
            9000,
          );
          return;
        }

        const found = checks.filter((x) => x.c.existing).map((x) => ({
          documentType: x.s.documentType, existing: x.c.existing!, next: x.c.nextSubmissionNo, reparse: x.c.reparse,
        }));
        if (found.length > 0) {
          setReplacing(found);
          setAltBranch('');
          return;
        }
      }

      const result = await gstSubmissionService.createBatch(engagement.id, {
        sourceDocumentId: document.id,
        registrationId,
        taxPeriodStart: period.start,
        taxPeriodEnd: period.end,
        sourceRef: branch,
        streams,
        confirmSupersede,
        revisionReason: confirmSupersede ? revisionReason : null,
        revisionNote: confirmSupersede && revisionNote.trim() ? revisionNote.trim() : null,
      });

      const empty = result.submissions.some((s) => (s.parseStats?.data ?? 0) === 0);
      if (empty) notificationService.toast.warning(result.message, 8000);
      else notificationService.toast.success(result.message, 6000);

      setReplacing(null);
      onCreated(result.submissions);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not create the submissions.', 8000);
    } finally {
      setWorking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagement, document, registrationId, periodKey, customStart, customEnd, streams, revisionReason, revisionNote, onCreated]);

  /** From the replace dialog: same file, same period, a branch that has nothing yet. */
  const addAsNewBranch = useCallback(() => {
    const b = cleanRef(altBranch);
    if (!b) return;
    if (branches.includes(b)) {
      notificationService.toast.warning(`${b} already has files for this GSTIN — choose a branch name that is new.`);
      return;
    }
    setBranchChoice(NEW_BRANCH);
    setNewBranch(b);
    setReplacing(null);
    void run(b, false);
  }, [altBranch, branches, run]);

  if (!isOpen || !engagement || !document) return null;

  const hasHsn = streams.some((s) => s.documentType === 'HSN_SUMMARY');

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        {(loading || working) && (
          <div
            style={{
              position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit',
            }}
          >
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{working ? 'Parsing…' : 'Reading the file…'}</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Create submissions — {document.fileName}</h2>
          <button className="modal-close" onClick={onClose} title="Close"><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div
            style={{
              border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
              borderRadius: 6, padding: '0.5rem 0.625rem', marginBottom: 12, fontSize: '0.75rem', color: 'var(--text-secondary)',
            }}
          >
            Each stream you choose below becomes one submission for this GSTIN, period and branch. Uploading the
            same branch again later makes a new <em>revision</em>; a different branch is added <em>alongside</em> and
            consolidated into the same return. Rows are parsed as-is; nothing is corrected here.
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <SyutSelect
                label={<>GSTIN <span className="text-danger">*</span></>}
                options={registrationOptions}
                value={registrationId}
                isMulti={false}
                isClearable={false}
                size="sm"
                onChange={(v) => { setRegistrationId((v as string) || ''); setPeriodKey(''); }}
              />
            </div>
            <div className="form-group">
              <SyutSelect
                label={<>Tax period <span className="text-danger">*</span></>}
                options={periodOptions}
                value={periodKey}
                isMulti={false}
                isClearable={false}
                size="sm"
                onChange={(v) => setPeriodKey((v as string) || '')}
                isDisabled={!registrationId}
              />
            </div>
          </div>

          {periodKey === CUSTOM_PERIOD && (
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">From</label>
                <input type="date" className="form-input" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">To</label>
                <input type="date" className="form-input" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
              </div>
            </div>
          )}

          {sample ? (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 10 }}>
              A <strong>sample</strong> for source setup: it is read and compared, never counted towards a return. Its branch is chosen
              when setup is finalised.
            </div>
          ) : (
          <div className="form-grid-2">
            <div className="form-group">
              {branches.length > 0 ? (
                <SyutSelect
                  label={<>Branch <span className="text-danger">*</span></>}
                  options={branchOptions}
                  value={branchChoice}
                  isMulti={false}
                  isClearable={false}
                  size="sm"
                  onChange={(v) => setBranchChoice((v as string) || '')}
                  isDisabled={!registrationId}
                />
              ) : (
                <>
                  <label className="form-label">Branch <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-input"
                    value={newBranch}
                    maxLength={50}
                    onChange={(e) => setNewBranch(cleanRef(e.target.value))}
                    disabled={!registrationId}
                  />
                </>
              )}
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                {branches.length > 0
                  ? <>This GSTIN already has files from <strong>{branches.join(', ')}</strong>. Pick one to revise its files, or <em>New branch</em> to add another location alongside.</>
                  : <>MAIN for a single-source client. Name the location (PZR, HO…) when several send files for one GSTIN.</>}
              </div>
            </div>
            {branches.length > 0 && branchChoice === NEW_BRANCH && (
              <div className="form-group">
                <label className="form-label">New branch name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  value={newBranch}
                  maxLength={50}
                  placeholder="e.g. TWMLA"
                  onChange={(e) => setNewBranch(cleanRef(e.target.value))}
                />
                {branches.includes(cleanRef(newBranch)) && (
                  <div className="form-error-message">{cleanRef(newBranch)} already exists — pick it from the list to revise it.</div>
                )}
              </div>
            )}
          </div>
          )}

          <div className="form-group">
            <label className="form-label">Sheets and what each one is <span className="text-danger">*</span></label>
            <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden' }}>
              {sheets.map((s, i) => (
                <div
                  key={s.name}
                  style={{
                    display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 260px', gap: 10, alignItems: 'center',
                    padding: '6px 10px', borderTop: i === 0 ? 'none' : '1px solid var(--border-color)',
                    opacity: s.hasTable && s.dataRows > 0 ? 1 : 0.6,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: '0.8125rem' }}>{s.name}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {s.hasTable
                        ? `${s.dataRows.toLocaleString()} data rows · header row ${s.headerRow} · ${s.headerCells.filter(Boolean).slice(0, 5).join(', ')}${s.headerCells.filter(Boolean).length > 5 ? '…' : ''}`
                        : 'No table on this sheet'}
                    </div>
                  </div>
                  <SyutSelect
                    options={STREAM_OPTIONS}
                    value={sheetStream[s.name] ?? SKIP}
                    isMulti={false}
                    isClearable={false}
                    size="sm"
                    isDisabled={!s.hasTable}
                    onChange={(v) => setSheetStream((prev) => ({ ...prev, [s.name]: (v as string) || SKIP }))}
                  />
                </div>
              ))}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
              {streams.length === 0
                ? 'Choose a stream for at least one sheet.'
                : <>Will create {streams.length} submission(s): {streams.map((s) => `${typeLabel(s.documentType)} (${s.sheetNames.length} sheet${s.sheetNames.length > 1 ? 's' : ''})`).join(' · ')}</>}
            </div>
          </div>

          {hasHsn && (
            <div className="form-group">
              <SyutSelect
                label="HSN summary basis"
                options={[{ value: 'GROSS', label: 'Gross — before credit notes' }, { value: 'NET', label: 'Net — after credit notes (Tally)' }]}
                value={summaryBasis}
                isMulti={false}
                isClearable={false}
                size="sm"
                onChange={(v) => setSummaryBasis((v as string) || 'GROSS')}
              />
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={working}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={() => { void run(effectiveRef, false); }}
            disabled={working || loading || !canSubmit || (!sample && branchChoice === NEW_BRANCH && branches.includes(sourceRef))}
          >
            Parse
          </button>
        </div>
      </div>

      {/* ---- replace existing revisions, or add as a new branch --------- */}
      {replacing && replacing.every((r) => r.reparse === 'CORRECTION') && (
        // The same file again, with a different sheet selection: the firm is
        // correcting its own parse, so there is no client reason to ask for
        // and no "different branch" question — the file is the same one.
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-md">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">Re-read this file with different sheets?</h2>
              <button className="modal-close" onClick={() => setReplacing(null)}><IconSystem.x size={20} /></button>
            </div>
            <div className="modal-content">
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
                <strong style={{ color: 'var(--text-primary)' }}>{document.fileName}</strong> is already parsed for this period and branch,
                from different sheets:
              </div>
              <ul style={{ fontSize: '0.8125rem', margin: '0 0 12px 18px', padding: 0 }}>
                {replacing.map((r) => (
                  <li key={r.documentType}>
                    <strong>{typeLabel(r.documentType)}</strong> — revision {r.existing.submissionNo} read{' '}
                    {r.existing.sheetNames?.length ? r.existing.sheetNames.join(', ') : 'other sheets'} → revision {r.next} reads{' '}
                    {streams.find((s) => s.documentType === r.documentType)?.sheetNames.join(', ')}
                  </li>
                ))}
              </ul>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Recorded as the firm's correction of its own sheet selection, not a change by the client. The earlier revision is kept.
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setReplacing(null)}>Back</button>
              <button className="btn btn-primary" onClick={() => { setRevisionReason(''); void run(sourceRef, true); }} disabled={working}>
                Re-parse as new revision
              </button>
            </div>
          </div>
        </>
      )}

      {replacing && !replacing.every((r) => r.reparse === 'CORRECTION') && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-md">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">Branch {sourceRef} already has files for this period</h2>
              <button className="modal-close" onClick={() => setReplacing(null)}><IconSystem.x size={20} /></button>
            </div>
            <div className="modal-content">
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
                Parsing this file under <strong style={{ color: 'var(--text-primary)' }}>{sourceRef}</strong> would replace:
              </div>
              <ul style={{ fontSize: '0.8125rem', margin: '0 0 12px 18px', padding: 0 }}>
                {replacing.map((r) => (
                  <li key={r.documentType}>
                    <strong>{typeLabel(r.documentType)}</strong> — revision {r.existing.submissionNo} from{' '}
                    {r.existing.sourceDocumentFileName ?? 'a file'} ({r.existing.parseStats?.data?.toLocaleString() ?? 0} data rows)
                    → becomes revision {r.next}
                    {r.reparse === 'CORRECTION' && <span style={{ color: 'var(--text-secondary)' }}> (this same file, different sheets)</span>}
                  </li>
                ))}
              </ul>

              <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.625rem', marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: '0.8125rem', marginBottom: 6 }}>Is this a different location?</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
                  Add it as a new branch instead — both stay, and both go into the same return.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    className="form-input"
                    value={altBranch}
                    maxLength={50}
                    placeholder="e.g. TWMLA"
                    onChange={(e) => setAltBranch(cleanRef(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <button className="btn btn-primary" onClick={addAsNewBranch} disabled={working || !cleanRef(altBranch)}>
                    Add as new branch
                  </button>
                </div>
              </div>

              <div style={{ fontWeight: 600, fontSize: '0.8125rem', marginBottom: 6 }}>Or replace — it is a corrected copy</div>
              <div className="form-group">
                <SyutSelect
                  label={<>Why is it being replaced? <span className="text-danger">*</span></>}
                  options={REVISION_REASONS}
                  value={revisionReason}
                  isMulti={false}
                  isClearable={false}
                  size="sm"
                  onChange={(v) => setRevisionReason((v as string) || '')}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Note</label>
                <input type="text" className="form-input" value={revisionNote} maxLength={1000} onChange={(e) => setRevisionNote(e.target.value)} />
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>The earlier revisions are kept, not deleted.</div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setReplacing(null)}>Back</button>
              <button className="btn btn-danger" onClick={() => { void run(sourceRef, true); }} disabled={working || !revisionReason}>
                Replace and parse
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default GstCreateSubmissionDialog;
